import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@amzn/awsui-components-react";
import {
  InvestigationAttachment,
  Stage,
} from "../../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../../lusca-search-api/LuscaSearchApiFactory";
import {
  AttachmentUploads,
  PendingAttachment,
} from "../../../hooks/useAttachmentUploads";
import { isImageType } from "./attachmentUtils";

/**
 * Pasted/dropped images go into the comment text as `![name](<attachment:REF>)`. REF is the
 * pending upload id while it uploads, then the attachment's S3 key. Only attachments of the
 * comment being rendered resolve — the reference never reaches the network as-is.
 */
export const INLINE_SCHEME = "attachment:";

const TOKEN_RE = /!\[[^\]\n]*\]\(<?attachment:([^)>\n]+)>?\)/g;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const safeAlt = (name: string) =>
  name.replace(/[[\]\\<>\n]/g, "").trim() || "image";

export const inlineToken = (name: string, ref: string) =>
  `![${safeAlt(name)}](<${INLINE_SCHEME}${ref}>)`;

/** Every REF the text shows inline. */
export const inlineRefs = (text: string): Set<string> =>
  new Set(Array.from(text.matchAll(TOKEN_RE), (m) => m[1]));

const tokenFor = (ref: string) =>
  new RegExp(`!\\[[^\\]\\n]*\\]\\(<?attachment:${escapeRe(ref)}>?\\)\\n?`, "g");

export const stripInline = (text: string, ref: string) =>
  text.replace(tokenFor(ref), "");

const replaceRef = (text: string, from: string, to: string) =>
  text.replace(
    new RegExp(`(\\]\\(<?attachment:)${escapeRe(from)}(>?\\))`, "g"),
    (_m, a: string, b: string) => `${a}${to}${b}`
  );

/** Attachments to list under a comment: those not already shown inline in its text. */
export const withoutInline = (
  attachments: InvestigationAttachment[] | undefined,
  text: string
): InvestigationAttachment[] => {
  const refs = inlineRefs(text);
  return (attachments || []).filter((a) => !refs.has(a.key));
};

interface InlineImage {
  url?: string;
  fileName?: string;
  uploading?: boolean;
  /** Saved attachment: its URL can be re-signed when it expires. */
  key?: string;
}

interface InlineContextValue {
  stage?: Stage;
  investigationId?: string;
  images: Record<string, InlineImage>;
}

const InlineAttachmentContext = React.createContext<InlineContextValue | null>(
  null
);

interface ProviderProps {
  stage?: Stage;
  investigationId?: string;
  /** Saved attachments of the comment being shown. */
  attachments?: InvestigationAttachment[];
  /** Files being composed (preview while writing). */
  pending?: PendingAttachment[];
  children: React.ReactNode;
}

export const InlineAttachmentProvider: React.FC<ProviderProps> = ({
  stage,
  investigationId,
  attachments,
  pending,
  children,
}) => {
  const value = useMemo(() => {
    const images: Record<string, InlineImage> = {};
    (attachments || []).forEach((a) => {
      images[a.key] = { url: a.url, fileName: a.fileName, key: a.key };
    });
    (pending || []).forEach((item) => {
      const image: InlineImage = {
        url: item.previewUrl || item.saved?.url,
        fileName: item.fileName,
        uploading: item.status === "uploading",
        key: item.key,
      };
      images[item.id] = image;
      if (item.key) images[item.key] = image;
    });
    return { stage, investigationId, images };
  }, [stage, investigationId, attachments, pending]);
  return (
    <InlineAttachmentContext.Provider value={value}>
      {children}
    </InlineAttachmentContext.Provider>
  );
};

const decode = (ref: string) => {
  try {
    return decodeURIComponent(ref);
  } catch {
    return ref;
  }
};

/** The <img> for an `attachment:` reference in rendered markdown. */
export const InlineAttachmentImage: React.FC<{
  attRef: string;
  alt?: string;
}> = ({ attRef, alt }) => {
  const ctx = useContext(InlineAttachmentContext);
  const ref = decode(attRef);
  const image = ctx?.images[ref] ?? ctx?.images[attRef];
  const [fresh, setFresh] = useState<string | undefined>();
  const refreshed = useRef(false);
  const url = fresh || image?.url;
  const label = alt || image?.fileName || "image";

  const refresh = () => {
    if (refreshed.current || !ctx?.stage || !ctx.investigationId) return;
    if (!image?.key) return;
    refreshed.current = true;
    InvestigationsApiFactory()
      .getInvestigationArtifactUrl(ctx.stage, ctx.investigationId, image.key)
      .then(({ data }) => setFresh(data.url))
      .catch(console.error);
  };

  // A saved attachment shown without a URL (e.g. the activity feed): sign one on demand.
  useEffect(() => {
    if (image && !image.url && !image.uploading) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per image
  }, [image?.key]);

  if (!image) {
    return (
      <Box variant="span" color="text-body-secondary" fontSize="body-s">
        [image: {label}]
      </Box>
    );
  }
  if (!url) {
    return (
      <Box variant="span" color="text-body-secondary" fontSize="body-s">
        {image.uploading ? `Uploading ${label}…` : `[image: ${label}]`}
      </Box>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="comment-md__img-link"
    >
      <img
        className="comment-md__img"
        src={url}
        alt={label}
        loading="lazy"
        onError={refresh}
      />
    </a>
  );
};

/**
 * Keeps inline image references and the upload list in step while composing:
 * - `insert` puts pasted/dropped images into the text at the cursor;
 * - a finished upload's reference switches from its pending id to its S3 key;
 * - a failed upload or a removed chip drops its reference from the text;
 * - deleting a reference from the text removes that attachment.
 */
export function useInlineAttachments(
  uploads: AttachmentUploads,
  text: string,
  setText: (next: string) => void
) {
  const inlined = useRef<Set<string> | null>(null);
  const keys = useRef<Map<string, string>>(new Map());
  if (inlined.current === null) {
    const refs = inlineRefs(text);
    inlined.current = new Set(
      uploads.items
        .filter((item) => refs.has(item.key || item.id))
        .map((item) => item.id)
    );
  }
  const textRef = useRef(text);
  textRef.current = text;

  const insert = useCallback(
    (
      items: PendingAttachment[],
      selection?: { start: number; end: number }
    ) => {
      const images = items.filter((item) => isImageType(item.contentType));
      if (!images.length) return;
      images.forEach((item) => inlined.current?.add(item.id));
      const tokens = images
        .map((item) => inlineToken(item.fileName, item.id))
        .join("\n");
      const { current } = textRef;
      const start = Math.min(
        selection?.start ?? current.length,
        current.length
      );
      const end = Math.min(selection?.end ?? start, current.length);
      const before = current.slice(0, start);
      const after = current.slice(end);
      const lead = before && !before.endsWith("\n") ? "\n" : "";
      const trail = after && !after.startsWith("\n") ? "\n" : "";
      setText(`${before}${lead}${tokens}${trail}${after}`);
    },
    [setText]
  );

  // Layout effect: the text is corrected in the same commit, so no stale reference is ever shown.
  useLayoutEffect(() => {
    let next = text;
    const set = inlined.current ?? new Set<string>();
    const byId = new Map(uploads.items.map((item) => [item.id, item]));
    Array.from(set).forEach((id) => {
      const item = byId.get(id);
      if (item?.key) keys.current.set(id, item.key);
      const key = keys.current.get(id);
      if (!item || item.status === "error") {
        next = stripInline(next, id);
        if (key) next = stripInline(next, key);
        set.delete(id);
        return;
      }
      const refs = inlineRefs(next);
      if (key && key !== id && refs.has(id)) {
        next = replaceRef(next, id, key);
        return;
      }
      if (!refs.has(id) && !(key && refs.has(key))) {
        set.delete(id);
        uploads.remove(id);
      }
    });
    // A draft restored mid-upload can hold references to uploads that no longer exist.
    inlineRefs(next).forEach((ref) => {
      if (ref.startsWith("att-") && !byId.has(ref))
        next = stripInline(next, ref);
    });
    if (next !== text) setText(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uploads.remove is stable
  }, [text, uploads.items]);

  return insert;
}
